"use client";
import { Tabs, Tab } from "@heroui/react";
import { usePathname } from "next/navigation";
import {
  getRecommendedLink,
  getLeaderDecklistsLink,
  getDeckRatiosLink,
} from "@/lib/recommendedLinkHelper";
import {
  IconStar,
  IconList,
  IconMathXDivideY2,
  IconVs,
} from "@tabler/icons-react";
import { Breadcrumbs, BreadcrumbItem } from "@heroui/react";
import "@/components/Decklists/decklists-nav.css";

export default function DecklistsNav({
  isRec,
  leaderId,
  leaderData,
  set,
  region = "east",
  author = "unknown",
}: {
  isRec: boolean;
  leaderId: string;
  leaderData: any;
  set: string;
  region?: string;
  author?: string;
}) {
  const pathname = usePathname();

  return (
    <div>
      {/* Apply the 'breadcrumbs-container' class to the div wrapping the breadcrumbs */}
      <div className="breadcrumbs-container">
        {getBreadcrumbs(pathname, leaderData, leaderId, set, region, author)}
      </div>
      <div className="my-4">
        <Tabs
          selectedKey={getSelectedKey(pathname)}
          classNames={{
            tabList: "gap-6 w-full relative rounded-none p-0 border-divider",
            cursor: "w-full bg-primary-500",
            tab: "max-w-fit px-0 h-10",
            tabContent:
              "group-data-[selected=true]:text-primary-500 group-data-[selected=true]:font-bold",
          }}
          variant={"underlined"}
          aria-label="Leader decklists navbar"
        >
          <Tab
            href={getRecommendedLink(leaderId, leaderData.name, set)}
            className="m-0 p-0"
            key="recommended"
            title={
              <div className="flex flex-row gap-2">
                <IconStar height={18} width={18} />
                <div>Recommended</div>
              </div>
            }
          />
          <Tab
            href={getLeaderDecklistsLink(leaderId, leaderData.name, set)}
            key="decklists"
            title={
              <div className="flex flex-row gap-2">
                <IconList height={18} width={18} />
                <div>Decklists</div>
              </div>
            }
          />
        </Tabs>
      </div>
    </div>
  );
}

function getSelectedKey(pathname: string) {
  return pathname.includes("/decklist/") ? "recommended" : "decklists";
}

function getBreadcrumbs(
  pathname: string,
  leaderData: any,
  leaderId: string,
  set: string,
  region: string,
  author: string,
) {
  if (pathname.includes("/decklists/leader/")) {
    // Extract game from pathname (e.g., /one-piece/decklists/leader/...)
    const gameMatch = pathname.match(/\/([^\/]+)\/decklists\/leader/);
    const gameName = gameMatch ? gameMatch[1] : "one-piece";

    return (
      <Breadcrumbs>
        <BreadcrumbItem href={`/${gameName}/decklists`}>
          Decklists
        </BreadcrumbItem>
        <BreadcrumbItem
          href={getLeaderDecklistsLink(leaderId, leaderData.name, set)}
        >
          {leaderData.name} ({leaderId})
        </BreadcrumbItem>
        {set && <BreadcrumbItem>{set}</BreadcrumbItem>}
      </Breadcrumbs>
    );
  }

  if (
    pathname.includes("/decklists/deck/") ||
    pathname.includes("/decklist/")
  ) {
    // Extract game from pathname
    const gameMatch = pathname.match(/\/([^\/]+)\/decklists\/(deck|decklist)/);
    const gameName = gameMatch ? gameMatch[1] : "one-piece";

    return (
      <Breadcrumbs>
        <BreadcrumbItem href={`/${gameName}/decklists`}>
          Decklists
        </BreadcrumbItem>
        <BreadcrumbItem
          href={getLeaderDecklistsLink(leaderId, leaderData.name, set)}
        >
          {leaderData.name} ({leaderId})
        </BreadcrumbItem>
        {set && (
          <BreadcrumbItem
            href={getLeaderDecklistsLink(leaderId, leaderData.name, set)}
          >
            {set}
          </BreadcrumbItem>
        )}
        <BreadcrumbItem>{author}&apos;s Decklist</BreadcrumbItem>
      </Breadcrumbs>
    );
  }

  return (
    <Breadcrumbs>
      <BreadcrumbItem>Home</BreadcrumbItem>
      <BreadcrumbItem>Decklists</BreadcrumbItem>
      <BreadcrumbItem>Jewelry Bonney</BreadcrumbItem>
      <BreadcrumbItem>OP09</BreadcrumbItem>
      <BreadcrumbItem>East</BreadcrumbItem>
      <BreadcrumbItem>Fuu&apos;s Decklist</BreadcrumbItem>
    </Breadcrumbs>
  );
}
